// TileModal — view-mode camera focus modal.
//
// Clicking a tile on the wall in view mode opens this modal: a big
// CameraTile (size="lg", static — no hover lift, no resize handles)
// alongside a right-pane "events-body" listing this single camera's
// recent events. Spring-in transition is owned by v7.css; we just
// mount/unmount.
//
// This wave is "events-body for one camera, no fake data". The right
// pane is a search input + a list rendered from
// eventsClient.listForCamera(doorbot_id). Day grouping mirrors the
// mockup's ".event-day-header" sections (Just now / Earlier today /
// Yesterday / older). No pagination yet — the BFF default page caps
// what we show; if `next_cursor` exists we surface a "Load more"
// affordance for completeness.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ApiError } from "@/api";
import { eventsClient, type CameraEvent } from "@/api/eventsClient";
import { CameraTile, type CameraTileChip } from "@/components/CameraTile";
import { SharedLiveVideo } from "@/components/SharedLiveVideo";
import type { DashboardTile } from "@/types/dashboard";

/** Tab cycle helper — keeps focus inside the modal card. */
function getFocusable(container: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

export interface TileModalCamera {
  doorbot_id: string;
  name: string;
  location_name?: string | null;
}

export interface TileModalProps {
  /** Opened by the wall on tile click. */
  tile: DashboardTile;
  camera: TileModalCamera;
  /** Optional chip carried over from the wall tile (e.g. "Person"). */
  chip?: CameraTileChip;
  /** Optional age label carried over (e.g. "12s"). */
  age?: string;
  onClose: () => void;
}

/** Day-bucket label for grouping events. */
function dayLabelFor(eventTime: Date, now: Date): string {
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const yesterday = new Date(startOfDay);
  yesterday.setDate(startOfDay.getDate() - 1);
  if (eventTime >= startOfDay) {
    // Within last 60 minutes → "Just now" bucket; mockup uses this for
    // the freshest cluster. Anything else today → "Earlier today".
    if (now.getTime() - eventTime.getTime() <= 60 * 60_000) return "Just now";
    return "Earlier today";
  }
  if (eventTime >= yesterday) return "Yesterday";
  return eventTime.toLocaleDateString();
}

/** "12s ago" / "5m ago" / "14:22" — same vocabulary as the mockup. */
function whenLabelFor(eventTime: Date, now: Date): string {
  const deltaMs = now.getTime() - eventTime.getTime();
  if (deltaMs < 60_000) {
    const s = Math.max(0, Math.floor(deltaMs / 1_000));
    return `${s}s\nago`;
  }
  if (deltaMs < 60 * 60_000) {
    return `${Math.floor(deltaMs / 60_000)}m\nago`;
  }
  // Within the day → wall-clock time. Across days → date + time.
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  if (eventTime >= startOfDay) {
    return eventTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return eventTime.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Map a CameraEvent's `kind` into one of the v7 ev-icon visuals. */
function kindClassFor(ev: CameraEvent): string {
  const k = (ev.kind ?? "").toLowerCase();
  if (k.includes("person") || ev.person_detected) return "kind-person";
  if (k.includes("vehicle") || k.includes("car")) return "kind-vehicle";
  if (k.includes("alarm") || k.includes("ding")) return "kind-alarm";
  return "kind-motion";
}

/** Human-friendly description for the event row. */
function descLabelFor(ev: CameraEvent): string {
  if (ev.person_detected) return "Person detected";
  if (!ev.kind) return "Motion detected";
  // AVA returns kinds like "motion" / "ding" / "vehicle_detected";
  // turn snake_case into Sentence case so the row reads naturally.
  const cleaned = ev.kind.replaceAll("_", " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Pick the best timestamp the event carries. */
function eventTimeOf(ev: CameraEvent): Date | null {
  if (ev.start_ms && ev.start_ms > 0) return new Date(ev.start_ms);
  if (ev.created_at) {
    const d = new Date(ev.created_at);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/** Tiny inline icon factory — kept here so the modal is self-contained. */
function evIcon(kindClass: string): JSX.Element {
  if (kindClass === "kind-person") {
    return (
      <span className={`ev-icon ${kindClass}`} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z" />
        </svg>
      </span>
    );
  }
  if (kindClass === "kind-vehicle") {
    return (
      <span className={`ev-icon ${kindClass}`} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M19 7H5L3 13v6h2v-2h14v2h2v-6l-2-6zM6.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
        </svg>
      </span>
    );
  }
  if (kindClass === "kind-alarm") {
    return (
      <span className={`ev-icon ${kindClass}`} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.1-1.6-5.6-4.5-6.3V4a1.5 1.5 0 1 0-3 0v.7C7.6 5.4 6 7.9 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
      </span>
    );
  }
  return (
    <span className={`ev-icon ${kindClass}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
      </svg>
    </span>
  );
}

export function TileModal({
  tile: _tile,
  camera,
  chip,
  age,
  onClose,
}: TileModalProps): JSX.Element {
  const [events, setEvents] = useState<CameraEvent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Load events for this camera. AbortController for fast modal flips.
  useEffect(() => {
    const ctrl = new AbortController();
    setEvents(null);
    setLoadError(null);
    // BFF requires a bounded time window. Default to "last 7 days → now".
    const end = new Date().toISOString();
    const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    eventsClient
      .listForCamera(camera.doorbot_id, { start, end, limit: 50, signal: ctrl.signal })
      .then((resp) => {
        if (ctrl.signal.aborted) return;
        setEvents(resp.items);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `${err.status} ${err.code ?? err.message}`
            : err instanceof Error
              ? err.message
              : "Failed to load events";
        setLoadError(msg);
        setEvents([]);
      });
    return () => ctrl.abort();
  }, [camera.doorbot_id]);

  // Escape closes; Tab cycles within the modal; restore focus on unmount.
  useEffect(() => {
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const handle = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const card = cardRef.current;
      if (!card) return;
      const focusables = getFocusable(card);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handle);
    // Defer focus so the modal is mounted before we move focus into it.
    const t = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);
    // Lock background scroll while the modal is open so the wall behind
    // doesn't scroll on touch devices.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handle);
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      previousActiveRef.current?.focus();
    };
  }, [onClose]);

  const handleScrimClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // Only close if the user clicked the scrim itself, not bubbled from
      // inside the modal card.
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const filteredEvents = useMemo<CameraEvent[]>(() => {
    if (!events) return [];
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) => {
      const k = (ev.kind ?? "").toLowerCase();
      return k.includes(q) || descLabelFor(ev).toLowerCase().includes(q);
    });
  }, [events, search]);

  const grouped = useMemo<{ label: string; events: CameraEvent[] }[]>(() => {
    if (filteredEvents.length === 0) return [];
    const now = new Date();
    const buckets = new Map<string, CameraEvent[]>();
    const order: string[] = [];
    for (const ev of filteredEvents) {
      const t = eventTimeOf(ev);
      const label = t ? dayLabelFor(t, now) : "Unknown time";
      if (!buckets.has(label)) {
        buckets.set(label, []);
        order.push(label);
      }
      buckets.get(label)!.push(ev);
    }
    return order.map((label) => ({
      label,
      events: buckets.get(label) ?? [],
    }));
  }, [filteredEvents]);

  // The modal lives at document.body so the wall's transformed parents
  // (sidebar pin uses CSS grid) don't constrain the fixed-position card.
  const modal = (
    <>
      <div
        className="v7-modal-scrim"
        onMouseDown={handleScrimClick}
        aria-hidden="true"
      />
      <div
        className="v7-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="v7-modal-title"
        ref={cardRef}
      >
        <button
          type="button"
          ref={closeButtonRef}
          className="v7-modal__close"
          aria-label="Close camera"
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z" />
          </svg>
        </button>
        <div className="v7-modal__hero">
          {/* Snapshot underlay (via the static CameraTile) shows through
              while WHEP is connecting / unavailable; the SharedLiveVideo
              overlays once the tile's PeerConnection has a remote
              stream. The same MediaStream is bound on the wall tile —
              no second AVA session is opened for the modal. */}
          <CameraTile
            camera={camera}
            mode="view"
            size="lg"
            staticTile
            chip={chip}
            age={age}
          />
          <SharedLiveVideo
            deviceId={camera.doorbot_id}
            className="v7-modal__hero-video"
          />
        </div>
        <div className="v7-modal__events">
          <div className="v7-modal__events-head">
            <h2 id="v7-modal-title">
              <span>Events</span>
              <span className="cam-name">
                {events === null
                  ? "Loading…"
                  : `${events.length} recent`}
              </span>
            </h2>
            <div className="cam-name">
              {camera.name}
              {camera.location_name ? ` · ${camera.location_name}` : ""}
            </div>
            <div className="v7-pane-search">
              <span className="ico" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search events for this camera…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search camera events"
              />
            </div>
          </div>
          <div className="v7-modal__events-list">
            {loadError && (
              <div className="v7-events-empty" role="alert">
                {loadError}
              </div>
            )}
            {!loadError && events === null && (
              <div className="v7-events-empty">Loading events…</div>
            )}
            {!loadError && events !== null && filteredEvents.length === 0 && (
              <div className="v7-events-empty">
                {events.length === 0
                  ? "No events for this camera yet."
                  : `No events match "${search}".`}
              </div>
            )}
            {grouped.map((group) => (
              <div key={group.label}>
                <div className="v7-event-day-header">{group.label}</div>
                {group.events.map((ev) => {
                  const t = eventTimeOf(ev);
                  const when = t ? whenLabelFor(t, new Date()) : "—";
                  const kindClass = kindClassFor(ev);
                  return (
                    <div key={ev.ding_id} className="v7-event-row">
                      <span className="when">
                        {when.split("\n").map((line, i) => (
                          <span key={i}>
                            {line}
                            {i === 0 && when.includes("\n") && <br />}
                          </span>
                        ))}
                      </span>
                      <div className="body">
                        <span className="desc">
                          {evIcon(kindClass)}
                          {descLabelFor(ev)}
                        </span>
                        <span className="meta">
                          {ev.has_recording ? "Recording available" : "Live event"}
                          {ev.duration_seconds > 0
                            ? ` · ${ev.duration_seconds}s`
                            : ""}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
